// draftDatabase.js - Firestore database operations for drafts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  arrayUnion
} from 'firebase/firestore';

// Import the existing Firebase configuration
import { db } from '../../../firebase';

/**
 * DRAFT DATABASE OPERATIONS
 * ========================
 * Firestore operations for managing draft metadata and history
 */

// Create a new draft record
export const createDraftRecord = async (draftData) => {
  try {
    console.log('DEBUG: Creating draft record with data:', draftData);

    const docRef = await addDoc(collection(db, 'guide_drafts'), {
      filename: draftData.filename,
      country: draftData.country,
      language: draftData.language,
      city: draftData.city || '',
      title: draftData.title,
      description: draftData.description || '',
      status: 'draft',
      createdBy: draftData.createdBy || 'anonymous',
      createdAt: serverTimestamp(),
      lastModified: serverTimestamp(),
      storageUrl: draftData.storageUrl,
      publishedUrl: null,
      pointsCount: draftData.pointsCount || 0,
      estimatedDuration: draftData.estimatedDuration || 0,
      reviewHistory: [
        {
          action: 'created',
          by: draftData.createdBy || 'anonymous',
          at: new Date().toISOString(),
          notes: 'Draft created'
        }
      ]
    });

    console.log('Draft record created with ID:', docRef.id);
    return { success: true, id: docRef.id };

  } catch (error) {
    console.error('Error creating draft record:', error);
    throw new Error(`Failed to create draft record: ${error.message}`);
  }
};

// Update draft record
export const updateDraftRecord = async (draftId, updates) => {
  try {
    const draftRef = doc(db, 'guide_drafts', draftId);

    const updateData = {
      ...updates,
      lastModified: serverTimestamp()
    };

    await updateDoc(draftRef, updateData);
    console.log('Draft record updated:', draftId);
    return { success: true };

  } catch (error) {
    console.error('Error updating draft record:', error);
    throw new Error(`Failed to update draft record: ${error.message}`);
  }
};

// Add review history entry
export const addReviewHistoryEntry = async (draftId, historyEntry) => {
  try {
    const draftRef = doc(db, 'guide_drafts', draftId);

    await updateDoc(draftRef, {
      reviewHistory: arrayUnion({
        ...historyEntry,
        at: new Date().toISOString()
      }),
      lastModified: serverTimestamp()
    });

    console.log('Review history entry added to draft:', draftId);
    return { success: true };

  } catch (error) {
    console.error('Error adding review history:', error);
    throw new Error(`Failed to add review history: ${error.message}`);
  }
};

// Update draft status
export const updateDraftStatus = async (draftId, newStatus, userId, notes = '') => {
  try {
    const historyEntry = {
      action: `status_changed_to_${newStatus}`,
      by: userId,
      notes: notes || `Status changed to ${newStatus}`
    };

    await updateDraftRecord(draftId, { status: newStatus });
    await addReviewHistoryEntry(draftId, historyEntry);

    return { success: true };

  } catch (error) {
    console.error('Error updating draft status:', error);
    throw new Error(`Failed to update draft status: ${error.message}`);
  }
};

// Get all drafts
export const getAllDrafts = async (filters = {}) => {
  try {
    console.log('DEBUG: Fetching drafts with filters:', filters);
    let q = collection(db, 'guide_drafts');

    // Apply filters
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    if (filters.country) {
      q = query(q, where('country', '==', filters.country));
    }
    if (filters.createdBy) {
      q = query(q, where('createdBy', '==', filters.createdBy));
    }

    // Order by last modified
    q = query(q, orderBy('lastModified', 'desc'));

    const querySnapshot = await getDocs(q);
    const drafts = [];

    querySnapshot.forEach((doc) => {
      drafts.push({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || null,
        lastModified: doc.data().lastModified?.toDate?.()?.toISOString() || null
      });
    });

    return drafts;

  } catch (error) {
    console.error('Error fetching drafts:', error);
    throw new Error(`Failed to fetch drafts: ${error.message}`);
  }
};

// Get single draft by ID
export const getDraftById = async (draftId) => {
  try {
    const draftRef = doc(db, 'guide_drafts', draftId);
    const docSnap = await getDoc(draftRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || null,
        lastModified: data.lastModified?.toDate?.()?.toISOString() || null
      };
    } else {
      throw new Error('Draft not found');
    }

  } catch (error) {
    console.error('Error fetching draft:', error);
    throw new Error(`Failed to fetch draft: ${error.message}`);
  }
};

// Delete draft record
export const deleteDraftRecord = async (draftId) => {
  try {
    await deleteDoc(doc(db, 'guide_drafts', draftId));
    console.log('Draft record deleted:', draftId);
    return { success: true };

  } catch (error) {
    console.error('Error deleting draft record:', error);
    throw new Error(`Failed to delete draft record: ${error.message}`);
  }
};

// Mark draft as published
export const markDraftAsPublished = async (draftId, publishedUrl, userId) => {
  try {
    const historyEntry = {
      action: 'published',
      by: userId,
      notes: 'Draft published to live site'
    };

    await updateDraftRecord(draftId, {
      status: 'published',
      publishedUrl: publishedUrl
    });
    await addReviewHistoryEntry(draftId, historyEntry);

    return { success: true };

  } catch (error) {
    console.error('Error marking draft as published:', error);
    throw new Error(`Failed to mark draft as published: ${error.message}`);
  }
};

// Get drafts by status
export const getDraftsByStatus = async (status) => {
  return getAllDrafts({ status });
};

// Get user's drafts
export const getUserDrafts = async (userId) => {
  return getAllDrafts({ createdBy: userId });
};

const draftDatabase = {
  createDraftRecord,
  updateDraftRecord,
  addReviewHistoryEntry,
  updateDraftStatus,
  getAllDrafts,
  getDraftById,
  deleteDraftRecord,
  markDraftAsPublished,
  getDraftsByStatus,
  getUserDrafts
};

export default draftDatabase;